/**
 * PieChartCard Component
 *
 * Reusable Pie Chart with Korat brand colors and responsive design
 */

import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts'

// Korat brand colors
const COLORS = {
  primary: ['#1e3a5f', '#2c5282', '#3b6ba8', '#4a84ce'], // Navy shades
  accent: ['#f97316', '#fb923c', '#fdba74', '#fed7aa'], // Orange shades
  mixed: ['#1e3a5f', '#f97316', '#2c5282', '#fb923c', '#3b6ba8', '#fdba74'], // Navy + Orange alternating
}

export interface PieChartData {
  name: string
  value: number
  color?: string
}

export interface PieChartCardProps {
  title: string
  description?: string
  data: PieChartData[]
  colorScheme?: 'primary' | 'accent' | 'mixed'
  showLegend?: boolean
  showPercentage?: boolean
  height?: number
  isLoading?: boolean
}

export function PieChartCard({
  title,
  description,
  data,
  colorScheme = 'mixed',
  showLegend = true,
  showPercentage = true,
  height = 300,
  isLoading = false,
}: PieChartCardProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-2 animate-pulse" />
        {description && <div className="h-4 bg-slate-100 rounded w-2/3 mb-4 animate-pulse" />}
        <div className="flex items-center justify-center" style={{ height }}>
          <div className="w-48 h-48 rounded-full bg-slate-200 animate-pulse" />
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-base font-semibold text-korat-navy-700">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
        <div className="flex items-center justify-center text-slate-400 text-sm" style={{ height }}>
          ไม่มีข้อมูล
        </div>
      </div>
    )
  }

  const total = data.reduce((sum, item) => sum + item.value, 0)

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (!showPercentage || percent < 0.05) return null // Hide labels < 5%

    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        className="text-xs font-semibold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null

    const data = payload[0].payload
    const percent = ((data.value / total) * 100).toFixed(1)

    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="text-sm font-semibold text-korat-navy-700">{data.name}</p>
        <p className="text-xs text-slate-600 mt-1">
          {new Intl.NumberFormat('th-TH').format(data.value)} ({percent}%)
        </p>
      </div>
    )
  }

  const colors = data.map((item, index) =>
    item.color || COLORS[colorScheme][index % COLORS[colorScheme].length]
  )

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-base font-semibold text-korat-navy-700">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{description}</p>}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={CustomLabel}
            outerRadius={height / 3}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((_entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
              formatter={(value) => <span className="text-sm font-medium text-slate-700">{value}</span>}
            />
          )}
        </PieChart>
      </ResponsiveContainer>

      {/* Total */}
      <div className="mt-4 pt-4 border-t border-slate-200 text-center">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">รวมทั้งหมด</p>
        <p className="text-2xl font-bold text-korat-navy-700 tabular-nums mt-1">
          {new Intl.NumberFormat('th-TH').format(total)}
        </p>
      </div>
    </div>
  )
}
